import { useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Loader2, Send, Film, Check } from "lucide-react";
import { useWorkSampleSelection } from "@/components/shared/useWorkSampleSelection";

const MAX_SAMPLES = 3;
const MAX_NOTE = 500;

const COPY = {
  application: {
    eyebrow: "APPLY", title: "Apply", button: "Send application", disclaimer: null,
    success: "Application sent.",
  },
  support_request: {
    eyebrow: "REQUEST NJM SUPPORT", title: "Request NJM support", button: "Send request →",
    disclaimer: "NJM will review your request. We can't guarantee an introduction or representation.",
    success: "Request sent. You'll see updates in My Work.",
  },
};

function WorkSampleTile({ sample, selected, onToggle }) {
  return (
    <button type="button" onClick={onToggle} data-testid={`opportunity-apply-sample-${sample.id}`}
      className={`relative aspect-video rounded-[8px] overflow-hidden border-2 transition-colors ${selected ? "border-brand" : "border-hairline hover:border-hairline-strong"}`}>
      {sample.thumbnail_url
        ? <img src={sample.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
        : <span className="absolute inset-0 flex items-center justify-center bg-ink/5"><Film className="h-5 w-5 text-faint" /></span>}
      <span className="absolute bottom-1 left-1 right-1 truncate rounded bg-black/55 px-1.5 py-0.5 text-[9px] font-semibold text-white">{sample.title || "Work sample"}</span>
      {selected && <span className="absolute top-1.5 right-1.5 grid place-items-center h-5 w-5 rounded-full bg-brand"><Check className="h-3 w-3 text-ink" /></span>}
    </button>
  );
}

/**
 * NJM Build Opportunities — Apply / Request NJM Support modal (2026-09-27, restyled
 * 2026-09-28 to match the approved design deck). Two headers, two CTAs, ONE component, per the
 * approved mockup. Shares `useWorkSampleSelection` (samples picker fetch + toggle logic) with
 * Collaborations' ApplyToSlotModal.jsx — the thumbnail TILE markup here is intentionally its
 * own (that mockup asks for tiles, Collaborations' own checkbox-list markup stays untouched).
 */
export function OpportunityApplyModal({ opportunity, kind, onClose, onSent, onGoToMyWork }) {
  const { samples, selected, toggle, loading } = useWorkSampleSelection(MAX_SAMPLES);
  const [workUrl, setWorkUrl] = useState("");
  const [note, setNote] = useState("");
  const [availableFrom, setAvailableFrom] = useState("");
  const [availableUntil, setAvailableUntil] = useState("");
  const [flexible, setFlexible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const copy = COPY[kind];

  const submit = async () => {
    const trimmedUrl = workUrl.trim();
    if (selected.length === 0 && !trimmedUrl) { toast.error("Pick 1–3 work samples or share a work link"); return; }
    if (note.length > MAX_NOTE) { toast.error(`Keep your note under ${MAX_NOTE} characters`); return; }
    if (!flexible && !availableFrom) { toast.error("Add a from-date, or mark yourself Flexible"); return; }
    setSubmitting(true);
    try {
      await api.post(`/v1/build-opportunities-public/${opportunity.id}/interest`, {
        kind, work_sample_ids: selected, work_url: trimmedUrl || null, note: note.trim() || null,
        available_from: availableFrom || null, available_until: availableUntil || null, flexible,
      });
      // Bugfix (2026-09-27, verification pass): `onSent` must NOT fire here — the parent's
      // handler closes/unmounts this modal, which previously destroyed the success screen
      // before it ever painted (setSent(true) and the parent's unmount landed in the same
      // React 18 batch). The success screen below now owns calling `onSent` itself, only once
      // the freelancer actually dismisses it.
      setSent(true);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "already_applied") toast.error("You already have an open request for this opportunity");
      else if (code === "weekly_limit_reached") toast.error("You've reached this week's limit of 5 requests — try again next week");
      else if (code === "invalid_work_url") toast.error("That work link doesn't look valid");
      else if (code === "note_too_long") toast.error(`Keep your note under ${MAX_NOTE} characters`);
      else if (code === "availability_required") toast.error("Add a from-date, or mark yourself Flexible");
      else if (code === "work_reference_required") toast.error("Pick 1–3 work samples or share a work link");
      else toast.error("Couldn't send this — try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/60 p-4" data-testid="opportunity-apply-success-overlay">
        <div className="w-full max-w-[400px] rounded-[14px] bg-paper shadow-2xl p-8 text-center space-y-4">
          <div className="mx-auto grid h-11 w-11 place-items-center rounded-full border-[1.5px] border-brand text-[#8A6A00]"><Check className="h-5 w-5" /></div>
          <div className="text-[15px] font-semibold text-ink" data-testid="opportunity-apply-success-message">{copy.success}</div>
          {kind === "support_request" ? (
            <div className="flex flex-col gap-2">
              <button data-testid="opportunity-apply-success-back" onClick={() => { onSent?.(); onClose(); }} className="w-full rounded-[8px] border border-ink px-4 py-2.5 text-sm font-semibold text-ink hover:bg-ink hover:text-paper transition-colors">Back to Opportunities</button>
              <button data-testid="opportunity-apply-success-mywork" onClick={() => { onSent?.(); onClose(); onGoToMyWork?.(); }} className="w-full rounded-[8px] bg-brand px-4 py-2.5 text-sm font-bold text-ink hover:bg-brand/90 transition-colors">Go to My Work</button>
            </div>
          ) : (
            <button data-testid="opportunity-apply-success-close" onClick={() => { onSent?.(); onClose(); }} className="w-full rounded-[8px] bg-brand px-4 py-2.5 text-sm font-bold text-ink hover:bg-brand/90 transition-colors">Done</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/60 p-4" onClick={onClose} data-testid="opportunity-apply-overlay">
      <div className="w-full max-w-[560px] rounded-[14px] bg-paper shadow-2xl max-h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="shrink-0 bg-ink border-t-[3px] border-brand flex items-center justify-between px-5 sm:px-6 py-5">
          <div>
            <div className="font-mont text-[9.5px] font-extrabold uppercase tracking-[0.16em] text-brand">{copy.eyebrow}</div>
            <div className="font-mont text-[15px] font-bold text-paper mt-1">{opportunity.title}</div>
          </div>
          <button onClick={onClose} data-testid="opportunity-apply-close" className="text-paper/60 hover:text-paper transition-colors text-lg leading-none">✕</button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 sm:p-6 space-y-5">
          <div>
            <label className="font-mont text-[10px] font-semibold uppercase tracking-[0.12em] text-faint">Show your work</label>
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-faint" /></div>
            ) : samples.length > 0 ? (
              <>
                <p className="mt-1 text-[11px] text-faint">Pick 1–3 · {selected.length} selected</p>
                <div className="mt-2 grid grid-cols-3 gap-2" data-testid="opportunity-apply-samples-grid">
                  {samples.map((s) => <WorkSampleTile key={s.id} sample={s} selected={selected.includes(s.id)} onToggle={() => toggle(s.id)} />)}
                </div>
              </>
            ) : (
              <div className="mt-2 text-xs text-faint" data-testid="opportunity-apply-no-samples">No saved work samples yet — paste a link instead.</div>
            )}
            <input value={workUrl} onChange={(e) => setWorkUrl(e.target.value)} type="url" placeholder="Or paste one work link — https://…" data-testid="opportunity-apply-work-url"
              className="mt-2.5 w-full rounded-[8px] border border-hairline bg-paper px-3.5 py-2.5 text-sm text-ink placeholder:text-faint focus:outline-none focus:border-ink transition-colors" />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="font-mont text-[10px] font-semibold uppercase tracking-[0.12em] text-faint">A short note</label>
              <span className="text-[10px] text-faint">{note.length}/{MAX_NOTE}</span>
            </div>
            <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE))} rows={3} placeholder="Why you'd be a good fit, your availability…" data-testid="opportunity-apply-note"
              className="mt-1.5 w-full rounded-[8px] border border-hairline bg-paper px-3.5 py-2.5 text-sm text-ink placeholder:text-faint focus:outline-none focus:border-ink transition-colors resize-none" />
          </div>

          <div>
            <label className="font-mont text-[10px] font-semibold uppercase tracking-[0.12em] text-faint">Availability</label>
            <div className="mt-1.5 flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-[12px] text-ink-soft cursor-pointer">
                <input type="checkbox" checked={flexible} onChange={(e) => setFlexible(e.target.checked)} data-testid="opportunity-apply-flexible" className="accent-brand" /> Flexible
              </label>
            </div>
            {!flexible && (
              <div className="mt-2 flex items-center gap-2">
                <input type="date" value={availableFrom} onChange={(e) => setAvailableFrom(e.target.value)} data-testid="opportunity-apply-available-from"
                  className="flex-1 rounded-[8px] border border-hairline bg-paper px-3 py-2 text-sm text-ink" />
                <input type="date" value={availableUntil} onChange={(e) => setAvailableUntil(e.target.value)} placeholder="Until (optional)" data-testid="opportunity-apply-available-until"
                  className="flex-1 rounded-[8px] border border-hairline bg-paper px-3 py-2 text-sm text-ink" />
              </div>
            )}
          </div>

          {copy.disclaimer && (
            <div className="rounded-[8px] bg-surface2 p-3.5 space-y-1">
              <p className="text-[11px] text-ink-soft leading-relaxed" data-testid="opportunity-apply-disclaimer">{copy.disclaimer}</p>
              <p className="text-[11px] text-faint">Shared with NJM only.</p>
            </div>
          )}
        </div>

        <div className="shrink-0 flex items-center justify-between gap-2 border-t border-hairline px-5 sm:px-6 py-4">
          <button onClick={onClose} data-testid="opportunity-apply-cancel" className="text-xs font-semibold text-ink-soft hover:text-ink transition-colors">Cancel</button>
          <button onClick={submit} disabled={submitting || loading} data-testid="opportunity-apply-submit"
            className="inline-flex items-center gap-1.5 rounded-[8px] bg-brand px-5 py-3 text-xs font-bold text-ink hover:bg-brand/90 transition-colors disabled:opacity-50">
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {copy.button}
          </button>
        </div>
      </div>
    </div>
  );
}
